/**
 * Utility functions for working with monitor analysis data.
 * Handles both legacy and new generic data structures.
 */

import type { MonitorAnalysis, MonitorImpact } from '@/types/bill'
import type { Config } from '@/api/types/config'

/**
 * Extract filter score configuration from config object
 */
export function getFilterScoreConfig(config?: Config | null): {
  filterScoreSection: string
  filterScoreField: string
} {
  return {
    filterScoreSection: config?.filter_score_section || 'summary',
    filterScoreField: config?.filter_score_field || 'overall_impact'
  }
}

/**
 * Get overall impact from monitor analysis (supports both old and new formats)
 * Now supports configurable section and field filtering
 */
export function getOverallImpact(
  analysis?: MonitorAnalysis,
  filterScoreSection?: string,
  filterScoreField?: string
): MonitorImpact | undefined {
  if (!analysis) return undefined
  
  // Use default values if not provided (backward compatibility)
  const sectionName = filterScoreSection || 'summary'
  const fieldName = filterScoreField || 'overall_impact'
  
  // New format: find specified section in analysis array
  if (Array.isArray(analysis.analysis)) {
    // For backward compatibility, if looking for 'summary' section, check 'overall' section instead
    const actualSectionName = sectionName === 'summary' ? 'overall' : sectionName
    const section = analysis.analysis.find(section => section.analysis_type === actualSectionName)
    if (section) {
      // For backward compatibility, if looking for 'overall_impact' field, check 'overall' score instead
      const actualFieldName = fieldName === 'overall_impact' ? 'overall' : fieldName
      const score = section.scores.find((score: any) => score.score_type === actualFieldName)
      if (score) {
        return {
          score: score.score,
          type: score.type
        }
      }
    }
    return undefined
  }
  
  // Legacy format: access by configured section and field
  const legacyAnalysis = analysis as any
  if (sectionName === 'summary' && fieldName === 'overall_impact') {
    // Default legacy behavior
    return legacyAnalysis.summary?.overall_impact
  } else {
    // Generic legacy access using configured section and field
    const section = legacyAnalysis.analysis?.[sectionName] || legacyAnalysis[sectionName]
    if (section) {
      // Try to access as nested field (e.g., impacts.fieldName)
      const impact = section.impacts?.[fieldName] || section[fieldName]
      if (impact && typeof impact === 'object' && 'score' in impact && 'type' in impact) {
        return impact
      }
    }
  }
  
  return undefined
}

/**
 * Get monitoring status from monitor analysis (supports both old and new formats)
 */
export function getShouldMonitor(analysis?: MonitorAnalysis): boolean {
  if (!analysis) return false
  
  // New format: find overall section and get should_monitor
  if (Array.isArray(analysis.analysis)) {
    const overallSection = analysis.analysis.find(section => section.analysis_type === 'overall')
    return overallSection?.should_monitor ?? false
  }
  
  // Legacy format: access summary.should_monitor
  const legacyAnalysis = analysis as any
  return legacyAnalysis.summary?.should_monitor ?? false
}

/**
 * Get analysis section by type (supports both old and new formats)
 */
export function getAnalysisSection(analysis: MonitorAnalysis | undefined, sectionType: string): any {
  if (!analysis) return undefined
  
  // New format: find section in analysis array
  if (Array.isArray(analysis.analysis)) {
    return analysis.analysis.find(section => section.analysis_type === sectionType)
  }
  
  // Legacy format: access directly by property name
  const legacyAnalysis = analysis as any
  return legacyAnalysis.analysis?.[sectionType]
}

/**
 * Get score from analysis section (supports both old and new formats)
 */
export function getScore(section: any, scoreType: string): MonitorImpact | undefined {
  if (!section) return undefined
  
  // New format: find score in scores array
  if (section.scores && Array.isArray(section.scores)) {
    const score = section.scores.find((s: any) => s.score_type === scoreType)
    if (score) {
      return {
        score: score.score,
        type: score.type
      }
    }
    return undefined
  }
  
  // Legacy format: access impacts.scoreType
  return section.impacts?.[scoreType]
}

/**
 * Get rationale from analysis section (supports both old and new formats)
 */
export function getRationale(section: any): string | undefined {
  return section?.rationale
}